import {
    Alert, AlertTitle, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
    DialogContent, DialogContentText, DialogTitle, Grid, Link, Radio, RadioGroup,
    FormControlLabel, TextField, Tooltip, IconButton
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import { format } from "date-fns";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { apiConstants, getGeneric, postGeneric } from "../../common/constants";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import PageTitle from "../../components/PageTitle";
import Loader from "../../common/Loader";

// Reads the server's answer rather than guessing from a role: authority here is
// membership of beneficiary.approval.users, which the frontend cannot know.
const ACCESS_URL = 'cod/beneficiary-approval/access';
const PENDING_URL = 'cod/beneficiary-approval/pending';
const OTP_URL = 'cod/beneficiary-approval/otp';
const APPROVE_URL = 'cod/beneficiary-approval/approve';
const DENY_URL = 'cod/beneficiary-approval/deny';
const APPROVE_BULK_URL = 'cod/beneficiary-approval/approve-bulk';

// Mirrors beneficiary.approval.bulk.max.size on the server, which is the limit
// that actually holds - this only keeps the user from composing a request that
// would be rejected.
const BULK_MAX = 25;

// No page size above BULK_MAX is offered. Select-all takes the page, so a page
// larger than one batch would hand the approver a selection he cannot send -
// which is exactly what happened on the first production run, when the grid
// held every pending account and select-all picked 46.
const PAGE_SIZE_OPTIONS = [10, 15, 20, BULK_MAX];

function formatDate(value) {
    if (!value) return "-";
    try {
        return format(new Date(value), 'yyyy-MM-dd HH:mm');
    } catch (e) {
        return "-";
    }
}

function errorOf(err, fallback) {
    return err?.response?.data?.errorMsg
        || err?.response?.data?.exception?.message
        || err?.response?.data?.message
        || err?.message
        || fallback;
}

export default function BeneficiaryApproval() {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(null);
    // Kept apart from hasAccess: a failed request is not a refusal, and
    // reporting one as the other sends people hunting through config for a
    // permission problem that does not exist.
    const [accessError, setAccessError] = useState(null);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(BULK_MAX);

    const [approveRow, setApproveRow] = useState(null);
    const [denyRow, setDenyRow] = useState(null);
    const [selection, setSelection] = useState([]);

    // The grid is handed ONLY the current page's rows. Previously it received
    // every pending account, so its header checkbox selected all 46 across all
    // pages - more than one batch can carry, and far more than anyone can
    // actually check before approving. Selection is cleared when the page or
    // page size changes, so what is ticked is always what is on screen.
    const goToPage = useCallback((nextPage) => {
        setSelection([]);
        setPage(nextPage);
    }, []);

    const changeSizePerPage = useCallback((nextSize) => {
        setSelection([]);
        setSizePerPage(nextSize);
    }, []);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);

    const loadPending = useCallback(() => {
        setIsLoading(true);
        getGeneric(PENDING_URL)
            .then((res) => {
                setRows(res.data?.responseObj || []);
                setSelection([]);
                setIsLoading(false);
            })
            .catch((err) => {
                setIsLoading(false);
                enqueueSnackbar(errorOf(err, 'Could not load pending approvals'), { variant: 'error' });
            });
    }, [enqueueSnackbar]);

    useEffect(() => {
        getGeneric(ACCESS_URL)
            .then((res) => {
                const allowed = res.data?.responseObj === true;
                setHasAccess(allowed);
                if (allowed) {
                    loadPending();
                } else {
                    setIsLoading(false);
                }
            })
            .catch((err) => {
                const status = err?.response?.status;
                setAccessError(
                    status === 404
                        ? 'The approvals API was not found on the server (404). The backend build running here does not include this feature yet.'
                        : status === 401 || status === 403
                            ? 'Your session is not accepted by the server (' + status + '). Sign in again.'
                            : 'Could not reach the approvals API' + (status ? ' (HTTP ' + status + ')' : '') + ': '
                              + errorOf(err, 'unknown error')
                );
                setHasAccess(false);
                setIsLoading(false);
            });
    }, [loadPending]);

    const columns = [
        {
            field: 'merchantOid', headerName: 'Merchant', flex: 1.4, minWidth: 200,
            renderCell: ({ row }) => (
                <div>
                    <div>{row.merchantName || row.merchantOid}</div>
                    {row.merchantName && (
                        <div style={{ fontSize: 11, color: '#66707a', fontFamily: 'monospace' }}>
                            {row.merchantOid}
                        </div>
                    )}
                </div>
            )
        },
        { field: 'beneficiaryName', headerName: 'Beneficiary', flex: 1.3, minWidth: 170 },
        {
            // Shown in full on purpose: the approver's job is to confirm the
            // destination account, and a masked number can only be checked to
            // four digits. Masked everywhere else - email, beneficiary list.
            field: 'accountNumber', headerName: 'Account Number', flex: 1.2, minWidth: 170,
            renderCell: ({ row }) => (
                <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                    {row.accountNumber}
                </span>
            )
        },
        { field: 'beneficiaryBankIfscCode', headerName: 'IFSC', flex: 0.9, minWidth: 120 },
        {
            field: 'beneficiaryBankIfscCodeRail', headerName: 'Mode', flex: 0.8, minWidth: 110,
            renderCell: ({ row }) => {
                // The rail follows from the IFSC, so it can be shown before the
                // bank is ever contacted.
                const idfc = (row.beneficiaryBankIfscCode || '').trim().toUpperCase().startsWith('IDFB');
                return <Chip size="small" label={idfc ? 'IFT' : 'NEFT / RTGS'} color={idfc ? 'primary' : 'default'} />;
            }
        },
        { field: 'approvalRequestedBy', headerName: 'Added by', flex: 1.2, minWidth: 170 },
        {
            field: 'approvalRequestedAt', headerName: 'Added on', flex: 1, minWidth: 140,
            renderCell: ({ row }) => <span>{formatDate(row.approvalRequestedAt)}</span>
        },
        {
            field: 'action', headerName: 'Decision', flex: 1, minWidth: 130, sortable: false,
            renderCell: ({ row }) => (
                <Box>
                    <Tooltip title="Approve and register with IDFC">
                        <IconButton size="small" onClick={() => setApproveRow(row)}>
                            <CheckCircleIcon fontSize="small" color="success" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Decline and remove">
                        <IconButton size="small" onClick={() => setDenyRow(row)}>
                            <CancelIcon fontSize="small" color="error" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ];

    if (hasAccess === false) {
        return (
            <>
                <PageTitle>Beneficiary Approvals</PageTitle>
                {accessError ? (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        <AlertTitle>Could not check access</AlertTitle>
                        {accessError}
                    </Alert>
                ) : (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <AlertTitle>Not authorised</AlertTitle>
                        Your account is not listed as an approver. Approvers are configured by the
                        Proship technical team in <code>beneficiary.approval.users</code>; the address
                        must match your Proship account email exactly.
                    </Alert>
                )}
            </>
        );
    }

    return (
        <>
            {isLoading && <Loader />}
            <PageTitle>Beneficiary Approvals</PageTitle>

            <ApproveDialog
                row={approveRow}
                onClose={() => setApproveRow(null)}
                onDone={() => { setApproveRow(null); loadPending(); }}
                enqueueSnackbar={enqueueSnackbar}
            />
            <BulkApproveDialog
                open={bulkOpen}
                rows={rows.filter((r, index) => selection.indexOf(index) !== -1)}
                onClose={() => setBulkOpen(false)}
                onDone={(result) => { setBulkOpen(false); setBulkResult(result); loadPending(); }}
                enqueueSnackbar={enqueueSnackbar}
            />
            <BulkResultDialog result={bulkResult} onClose={() => setBulkResult(null)} />
            <DenyDialog
                row={denyRow}
                onClose={() => setDenyRow(null)}
                onDone={() => { setDenyRow(null); loadPending(); }}
                enqueueSnackbar={enqueueSnackbar}
            />

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
                        <Grid item xs={12} md={6}>
                            <Box sx={{ fontSize: 14, color: 'text.secondary' }}>
                                These accounts have been added by finance but sent to no bank.
                                They cannot be used for COD remittance until approved.
                            </Box>
                        </Grid>
                        <Grid item>
                            {selection.length > 0 && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => setBulkOpen(true)}
                                    disabled={selection.length > BULK_MAX}
                                    sx={{ mr: 1 }}
                                >
                                    Approve {selection.length} selected
                                </Button>
                            )}
                            <Button startIcon={<RefreshIcon />} onClick={loadPending} size="medium">
                                Refresh
                            </Button>
                        </Grid>
                    </Grid>

                    {selection.length > BULK_MAX && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Approve at most {BULK_MAX} at a time. You have selected {selection.length}.
                            Untick some rows to continue.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {!isLoading && rows.length === 0 && (
                <Alert severity="success">Nothing is waiting for approval.</Alert>
            )}

            {rows.length > 0 && (
                <ProzoDataGrid
                    columns={columns}
                    autoHeight={true}
                    checkboxSelection={true}
                    disableSelectionOnClick={true}
                    selectionModel={selection}
                    onSelectionModelChange={(model) => setSelection(model)}
                    rows={rows
                        .map((row, index) => ({ ...row, id: index }))
                        .slice(page * sizePerPage, (page + 1) * sizePerPage)}
                    sizePerPage={sizePerPage}
                    setSizePerPage={changeSizePerPage}
                    setPage={goToPage}
                    page={page}
                    pagination={true}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    hideFooterPagination={false}
                    filterMode={"server"}
                    getRowHeight={() => 60}
                    rowCount={rows.length}
                    getRowId={(row) => row.id}
                />
            )}
        </>
    );
}

// Approve: request an OTP, then confirm it. Two steps in one dialog so the
// approver never loses sight of which account they are authorising.
function ApproveDialog({ row, onClose, onDone, enqueueSnackbar }) {
    const [channel, setChannel] = useState('SMS');
    const [tokenId, setTokenId] = useState(null);
    const [sentTo, setSentTo] = useState(null);
    const [otp, setOtp] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!row) {
            setChannel('SMS'); setTokenId(null); setSentTo(null); setOtp(''); setBusy(false);
        }
    }, [row]);

    if (!row) return null;

    const sendOtp = () => {
        setBusy(true);
        postGeneric(`${OTP_URL}?merchantOid=${encodeURIComponent(row.merchantOid)}&hash=${encodeURIComponent(row.hashData)}&channel=${channel}`)
            .then((res) => {
                const body = res.data?.responseObj || {};
                setTokenId(body.tokenId);
                setSentTo(body.sentTo);
                setBusy(false);
                enqueueSnackbar(`OTP sent to ${body.sentTo}`, { variant: 'success' });
            })
            .catch((err) => {
                setBusy(false);
                enqueueSnackbar(errorOf(err, 'Could not send OTP'), { variant: 'error' });
            });
    };

    const confirm = () => {
        setBusy(true);
        postGeneric(`${APPROVE_URL}?merchantOid=${encodeURIComponent(row.merchantOid)}&hash=${encodeURIComponent(row.hashData)}&tokenId=${encodeURIComponent(tokenId)}&otp=${encodeURIComponent(otp)}`)
            .then(() => {
                setBusy(false);
                enqueueSnackbar('Beneficiary approved and registered with IDFC', { variant: 'success' });
                onDone();
            })
            .catch((err) => {
                setBusy(false);
                // The OTP stays valid for a retry, so the dialog is left open.
                enqueueSnackbar(errorOf(err, 'Approval failed'), { variant: 'error' });
            });
    };

    return (
        <Dialog open={Boolean(row)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Approve beneficiary</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Approving sends these details to IDFC and allows COD remittance to this account.
                </DialogContentText>

                <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                        <Grid container spacing={1} sx={{ fontSize: 14 }}>
                            <Grid item xs={5}><strong>Merchant</strong></Grid>
                            <Grid item xs={7}>
                                <div>{row.merchantName || row.merchantOid}</div>
                                {row.merchantName && (
                                    // The oid stays visible so the approver can quote
                                    // it when checking against anything else.
                                    <div style={{ fontSize: 12, color: '#66707a', fontFamily: 'monospace' }}>
                                        {row.merchantOid}
                                    </div>
                                )}
                            </Grid>
                            <Grid item xs={5}><strong>Beneficiary</strong></Grid>
                            <Grid item xs={7}>{row.beneficiaryName}</Grid>
                            <Grid item xs={5}><strong>Account Number</strong></Grid>
                            <Grid item xs={7} sx={{ fontFamily: 'monospace' }}>{row.accountNumber}</Grid>
                            <Grid item xs={5}><strong>IFSC</strong></Grid>
                            <Grid item xs={7}>{row.beneficiaryBankIfscCode}</Grid>
                            <Grid item xs={5}><strong>Added by</strong></Grid>
                            <Grid item xs={7}>{row.approvalRequestedBy}</Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {!tokenId && (
                    <>
                        <DialogContentText sx={{ mb: 1 }}>
                            Where should the one-time password be sent?
                        </DialogContentText>
                        <RadioGroup row value={channel} onChange={(e) => setChannel(e.target.value)}>
                            <FormControlLabel value="SMS" control={<Radio />} label="Mobile (SMS)" />
                            <FormControlLabel value="EMAIL" control={<Radio />} label="Email" />
                        </RadioGroup>
                    </>
                )}

                {tokenId && (
                    <>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            A one-time password has been sent to <strong>{sentTo}</strong>.
                        </Alert>
                        <TextField
                            autoFocus
                            fullWidth
                            label="Enter OTP"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                            inputProps={{ maxLength: 8, inputMode: 'numeric' }}
                        />
                        <Box sx={{ mt: 1 }}>
                            <Link component="button" type="button" underline="hover"
                                onClick={() => { setTokenId(null); setOtp(''); }}>
                                Send again or use a different channel
                            </Link>
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" disabled={busy}>Cancel</Button>
                {!tokenId && (
                    <LoadingButton onClick={sendOtp} variant="contained" loading={busy}>
                        Send OTP
                    </LoadingButton>
                )}
                {tokenId && (
                    <LoadingButton onClick={confirm} variant="contained" color="success"
                        loading={busy} disabled={otp.length < 4}>
                        Approve
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
}

// Deny: no OTP, because nothing is sent to the bank and the action is
// reversible by adding the beneficiary again.
function DenyDialog({ row, onClose, onDone, enqueueSnackbar }) {
    const [remark, setRemark] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!row) { setRemark(''); setBusy(false); }
    }, [row]);

    if (!row) return null;

    const confirm = () => {
        setBusy(true);
        const remarkParam = remark ? `&remark=${encodeURIComponent(remark)}` : '';
        postGeneric(`${DENY_URL}?merchantOid=${encodeURIComponent(row.merchantOid)}&hash=${encodeURIComponent(row.hashData)}${remarkParam}`)
            .then(() => {
                setBusy(false);
                enqueueSnackbar('Beneficiary declined and removed', { variant: 'success' });
                onDone();
            })
            .catch((err) => {
                setBusy(false);
                enqueueSnackbar(errorOf(err, 'Could not decline beneficiary'), { variant: 'error' });
            });
    };

    return (
        <Dialog open={Boolean(row)} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Decline beneficiary</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    This removes the account from <strong>{row.merchantName || row.merchantOid}</strong>
                    {row.merchantName && (
                        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#66707a' }}>
                            {' '}({row.merchantOid})
                        </span>
                    )}.
                    Nothing has been sent to the bank, and finance will be notified so they can add it
                    again with corrected details.
                </DialogContentText>
                <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Reason (optional, included in the email to finance)"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" disabled={busy}>Cancel</Button>
                <LoadingButton onClick={confirm} variant="contained" color="error" loading={busy}>
                    Decline and remove
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

// Bulk approve: confirm the exact list, then send it.
//
// No OTP here, unlike single approval. Registering 200+ merchants during the
// Axis to IDFC migration one code at a time is not a workable ask, so the
// control is the review step instead: the approver sees every account in the
// batch, in full, before anything is sent to the bank.
function BulkApproveDialog({ open, rows, onClose, onDone, enqueueSnackbar }) {
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) setBusy(false);
    }, [open]);

    if (!open || !rows || rows.length === 0) return null;

    const confirm = () => {
        setBusy(true);
        const payload = rows.map((row) => ({ merchantOid: row.merchantOid, hash: row.hashData }));
        postGeneric(APPROVE_BULK_URL, payload)
            .then((res) => {
                setBusy(false);
                const body = res.data?.responseObj || {};
                enqueueSnackbar(
                    `${body.approved || 0} approved` + (body.failed ? `, ${body.failed} failed` : ''),
                    { variant: body.failed ? 'warning' : 'success' });
                onDone(body);
            })
            .catch((err) => {
                setBusy(false);
                enqueueSnackbar(errorOf(err, 'Bulk approval failed'), { variant: 'error' });
            });
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>Approve {rows.length} beneficiar{rows.length === 1 ? 'y' : 'ies'}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Each of these accounts will be sent to IDFC and will become the primary account
                    for its merchant. Check the account numbers before continuing.
                </DialogContentText>

                <Box sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid #e4e7eb', borderRadius: 1 }}>
                    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <Box component="thead" sx={{ backgroundColor: '#f8fafc' }}>
                            <Box component="tr">
                                <Box component="th" sx={{ textAlign: 'left', p: 1.2 }}>Merchant</Box>
                                <Box component="th" sx={{ textAlign: 'left', p: 1.2 }}>Beneficiary</Box>
                                <Box component="th" sx={{ textAlign: 'left', p: 1.2 }}>Account</Box>
                                <Box component="th" sx={{ textAlign: 'left', p: 1.2 }}>IFSC</Box>
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {rows.map((row, i) => (
                                <Box component="tr" key={i} sx={{ borderTop: '1px solid #f0f2f5' }}>
                                    <Box component="td" sx={{ p: 1.2 }}>{row.merchantName || row.merchantOid}</Box>
                                    <Box component="td" sx={{ p: 1.2 }}>{row.beneficiaryName}</Box>
                                    <Box component="td" sx={{ p: 1.2, fontFamily: 'monospace' }}>{row.accountNumber}</Box>
                                    <Box component="td" sx={{ p: 1.2 }}>{row.beneficiaryBankIfscCode}</Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                    <AlertTitle>Before you continue</AlertTitle>
                    This is recorded against your user. Accounts are sent to the bank one at a time,
                    so this may take a few seconds. Any that the bank rejects stay in this list with
                    the reason, and can be approved again once corrected.
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="outlined" disabled={busy}>Cancel</Button>
                <LoadingButton onClick={confirm} variant="contained" color="success" loading={busy}>
                    Approve {rows.length}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

// Per-row outcome. Shown even when everything succeeded, because "25 approved"
// is worth seeing explicitly before trusting that a migration batch landed.
function BulkResultDialog({ result, onClose }) {
    if (!result) return null;
    const results = result.results || [];
    const failures = results.filter((r) => !r.success);

    return (
        <Dialog open={Boolean(result)} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Bulk approval result</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Chip label={`${result.approved || 0} approved`} color="success" sx={{ mr: 1 }} />
                    {result.failed > 0 && <Chip label={`${result.failed} failed`} color="error" />}
                </Box>

                {failures.length > 0 && (
                    <>
                        <DialogContentText sx={{ mb: 1 }}>
                            These were not registered and remain awaiting approval:
                        </DialogContentText>
                        <Box sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e4e7eb', borderRadius: 1, p: 1 }}>
                            {failures.map((f, i) => (
                                <Box key={i} sx={{ py: 0.8, borderBottom: '1px solid #f0f2f5', fontSize: 13 }}>
                                    <strong>{f.beneficiaryName || f.merchantOid}</strong>
                                    <div style={{ color: '#b3261e' }}>{f.message}</div>
                                </Box>
                            ))}
                        </Box>
                    </>
                )}

                {failures.length === 0 && (
                    <Alert severity="success">
                        Every selected beneficiary was registered with IDFC and is now the primary
                        account for its merchant.
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Close</Button>
            </DialogActions>
        </Dialog>
    );
}
